import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import {Button, Modal} from 'antd';

import {ErrorAlert} from '../common/ErrorAlert';
import {
  Attachment,
  ChecklistSummaries,
  ChecklistSummary,
  ErrorResponse,
  Rental,
  RentalDocumentSendout,
  RentalDocumentType,
  RentalItem,
} from '../api/dtos/dtos';
import locales from '../locales';
import furoApi from '../api/FuroApi';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import * as paths from '../paths';
import {IdPageUrlParams} from '../paths';
import RentalDetailsEditor from './RentalDetailsEditor';
import ButtonBar from 'src/common/ButtonBar';
import RentalTransportLetterEditor from './RentalTransportLetterEditor';
import {setAppHeaderSubtitle} from '../app/AppHeader';
import {
  showDeleteConfirm,
  showDeleteSuccessNotification,
  showSaveConfirm,
  showSaveSuccessNotification
} from "../utils/dialogs";
import {RentalState} from "../common/state";
import accountProvider from "../account/AccountProvider";
import {idFromPathParams} from "../utils/router-utils";
import DocumentUploader from "../document-sendout/DocumentUploader";
import ChecklistSummaryComponent from "../checklist/ChecklistSummaryComponent";
import DocumentSendoutSummaryComponent from "../document-sendout/DocumentSendoutSummaryComponent";
import {openPdf} from "../utils/file-utils";
import {
  AlertController,
  bindAlertController,
  GlobalAlertContextConsumer
} from "../app/alert/GlobalAlertContextProvider";
import {PaymentType} from "../common/PaymentType";

interface RentalDetailsPageState {
  editable: boolean;
  rental?: Rental;
  editedRental?: Rental;
  error?: ErrorResponse;
  transportFormVisible: boolean;
  showDeposit: boolean;
  checklistSummary?: ChecklistSummary;
  documents?: RentalDocumentSendout[];
}

class RentalDetailsPage extends React.Component<RouteComponentProps<IdPageUrlParams>, RentalDetailsPageState> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public readonly alertController?: AlertController;

  constructor(props: RouteComponentProps<{}>) {
    super(props);
    this.state = {
      editable: false,
      showDeposit: false,
      transportFormVisible: false,
    };
  }

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {editable, editedRental, transportFormVisible, documents, error, checklistSummary, showDeposit} = this.state;
    const rentalDeleted = editedRental ? editedRental.deleted : false;
    const isAuthorised = accountProvider.isAdmin() || accountProvider.isSales();

    const downloadTermsAndConditions = () => this.downloadDocument(RentalDocumentType.TERMS_AND_CONDITIONS);
    const downloadMaintenanceGuide = () => this.downloadDocument(RentalDocumentType.MAINTENANCE_GUIDE);
    const downloadRentalContract = () => this.downloadDocument(RentalDocumentType.RENTAL_CONTRACT);
    const downloadDepositRequest = () => this.downloadDocument(RentalDocumentType.DEPOSIT_REQUEST);

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <GlobalAlertContextConsumer>
          {(state) => bindAlertController(state.checklist, this)}
        </GlobalAlertContextConsumer>

        {editedRental && (
          <Modal
            title={locales.transport_form}
            visible={transportFormVisible}
            footer={null}
            onCancel={this.onTransportFormClose}
            destroyOnClose={true}
          >
            <RentalTransportLetterEditor
              rentalId={editedRental.id!}
              siteAddress={editedRental.site && editedRental.site.address}
              onClose={this.onTransportFormClose}
            />
          </Modal>
        )}

        {isAuthorised && <ButtonBar style={{marginBottom: 24}}>
            <Button
                icon="file-pdf"
                onClick={downloadTermsAndConditions}
                disabled={editable}
            >{locales.document_type_terms}</Button>

          {showDeposit && <Button
              icon="file-pdf"
              onClick={downloadDepositRequest}
              disabled={editable}
          >{locales.document_type_deposit_request}</Button>
          }

            <Button
                icon="file-pdf"
                onClick={downloadMaintenanceGuide}
                disabled={editable}
            >{locales.document_type_maintenance}</Button>

            <Button
                icon="file-pdf"
                onClick={downloadRentalContract}
                disabled={editable}
            >{locales.document_type_rental_contract}</Button>

            <Button
                icon="solution"
                onClick={this.onPrintTransportClick}
                disabled={editable}
            >{locales.transport_form}</Button>

          {this.isDeleteButtonVisible() && (
            <Button
              type="danger"
              icon="delete"
              onClick={this.onDeleteClick}
              disabled={editable}
            >{locales.delete}</Button>
          )}

          {!rentalDeleted && (
            <Button
              type="primary"
              icon="edit"
              onClick={this.onEditClick}
              disabled={editable}
            >{locales.edit}</Button>
          )}
        </ButtonBar>}


        <RentalDetailsEditor
          mode="edit"
          rental={editedRental}
          checklistSummary={checklistSummary}
          showChecklist={true}
          onRentalChange={this.onRentalChange}
          editable={editable}
          onSubmit={this.onFormSubmit}
          onCancel={this.onFormCancel}
          onChecklistAdd={this.onCreateChecklistClick}
          onChecklistEdit={this.onEditChecklistClick}
        />

        {checklistSummary &&
            <ChecklistSummaryComponent
                summary={checklistSummary}
                onSendoutChecklists={this.onSendoutChecklists}
                style={{marginBottom: 24}}
                rentalDeleted={editedRental?.deleted}
            />
        }
        {documents &&
            <DocumentSendoutSummaryComponent
                documents={documents}
                onSendoutDocument={this.onSendDocuments}
                style={{marginBottom: 24}}
                rentalDeleted={editedRental?.deleted}
            />
        }

        {editedRental?.id &&
            <DocumentUploader
                id={editedRental.id}
                onDelete={this.onAttachmentDelete}
                action={this.onAttachmentUpload}
                onLoad={this.loadAttachments}
            />
        }

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private loadAttachments = (id: number): Promise<Attachment[]> => {
    return furoApi.rentals.getAttachments(id);
  }

  private onAttachmentDelete = (id: number, attachmentId: number): Promise<void> => {
    return furoApi.rentals.deleteAttachment(id, attachmentId);
  }

  private onAttachmentUpload = (id: number): string => {
    return furoApi.rentals.uploadAttachmentsUrl(id);
  }

  private isDeleteButtonVisible = (): boolean => {
    const {editedRental} = this.state;
    if (!editedRental || !editedRental.rentalItems) {
      return false;
    }
    if (editedRental.deleted) {
      // If rental is deleted we don't wanna show the delete button
      return false;
    }
    // If any of the rental items is returned delete is not possible
    return !editedRental.rentalItems.some(ri => ri.state === RentalState.RETURNED);

  };

  private downloadDocument = (documentType: RentalDocumentType) => {
    const {rental} = this.state;
    if (rental) {
      const promise = furoApi.document.getRentalDocument(rental.id!, documentType)
        .then(openPdf)
        .catch(this.onApiError);
      withLoadingOverlay(promise, this.loadingHandler);
    }
  };

  private onPrintTransportClick = () => {
    this.setState({transportFormVisible: true,});
  };

  private reload = () => {
    this.loadRental();
    this.loadChecklistSummary();
    this.loadDocuments();
  };

  private loadRental = () => {
    const rentalID = idFromPathParams(this.props);
    const promise = furoApi.rentals.get(rentalID)
      .then(this.onLoadRentalSuccess)
      .catch(this.onApiError)
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadRentalSuccess = (response: Rental) => {
    this.setState({
      rental: response,
      editedRental: response,
      showDeposit: (response.paymentType === PaymentType.TRANSFER && response.rentalItems?.some((i) => i.caution !== undefined && i.caution > 0)) ?? false,
      error: undefined,
    });
    setAppHeaderSubtitle(this.props, response.idString);
  };

  private loadChecklistSummary = () => {
    const rentalID = idFromPathParams(this.props);
    const promise = furoApi.checklist.getRentalChecklists(rentalID)
      .then(this.onLoadChecklistSummaries)
      .catch(this.onApiError)
    withLoadingOverlay(promise, this.loadingHandler);
  };


  private onLoadChecklistSummaries = (response: ChecklistSummaries) => {
    this.setState({
      checklistSummary: response.rental,
      error: undefined,
    });
  };

  private loadDocuments = () => {
    const rentalID = idFromPathParams(this.props);
    const promise = furoApi.document.getRentalDocumentSendouts(rentalID)
      .then(this.onLoadDocuments)
      .catch(this.onApiError)
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadDocuments = (response: RentalDocumentSendout[]) => {
    this.setState({
      documents: response,
      error: undefined,
    });
  };

  private onRentalChange = (rental: Rental) => {
    this.setState({editedRental: rental,});
  };

  private onEditClick = () => {
    this.setEditable(true);
  };

  private onFormCancel = () => {
    this.setEditable(false);
    this.setState({editedRental: this.state.rental});
  };

  private setEditable = (editable: boolean) => {
    this.setState({editable,});
  };

  private onFormSubmit = () => {
    showSaveConfirm(this.saveFormValues);
  };

  private onSendoutChecklists = () => {
    this.props.history.push(paths.viewRentalChecklist(this.state.rental?.id));
  };

  private onSendDocuments = () => {
    this.props.history.push(paths.viewRentalDocuments(this.state.rental?.id));
  };

  private saveFormValues = () => {
    const {editedRental} = this.state;
    if (!editedRental) {
      return;
    }
    const promise = furoApi.rentals.save(editedRental)
      .then(() => {
        showSaveSuccessNotification();
        this.onSaveSuccess();
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onSaveSuccess = () => {
    this.reload();
    this.setEditable(false);
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onTransportFormClose = () => {
    this.setState({
      transportFormVisible: false,
    });
  };

  private onDeleteClick = () => {
    showDeleteConfirm(this.deleteRental);
  };

  private deleteRental = () => {
    const {editedRental} = this.state;
    if (!editedRental) {
      return;
    }
    const promise = furoApi.rentals.delete(editedRental.id!!)
      .then(() => {
        showDeleteSuccessNotification();
        this.alertController?.refreshAlertCount();
        this.onSaveSuccess();
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onCreateChecklistClick = (rentalItem: RentalItem) => {
    const {rental} = this.state;

    this.props.history.push(paths.createChecklistPath({rentalItemId: rentalItem.id!, rentalId: rental?.id!}))
  };

  private onEditChecklistClick = (checklistId: number) => {
    this.props.history.push(paths.viewChecklistPath(checklistId));
  };

}

export default withRouter(RentalDetailsPage);




// WEBPACK FOOTER //
// ./src/rental-details/RentalDetailsPage.tsx